import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API, timeout: 30000 });

// Attach stored JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pm_token");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Surface 5xx errors once; on 401 clear session and redirect to login.
// Exception: /auth/me — AuthGuard owns that failure and redirects via
// <Navigate state={{ from }}> so the deep link survives re-login; a hard
// window.location redirect here would discard the router state.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("pm_token");
      localStorage.removeItem("pm_user");
      const isAuthMe = err.config?.url?.includes("/auth/me");
      if (!isAuthMe && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    } else if (!err.response || err.response.status >= 500) {
      toast.error("A server error occurred. Please try again.");
    }
    return Promise.reject(err);
  }
);

// Auth
export const authLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authForgotPassword = (email) =>
  api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const authResetPassword = (token, password) =>
  api.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const authChangePassword = (current_password, new_password) =>
  api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

export const getDashboard = () => api.get("/dashboard").then((r) => r.data);
export const listArtifacts = (params = {}) =>
  api.get("/artifacts", { params }).then((r) => r.data);
export const getArtifact = (id) => api.get(`/artifacts/${id}`).then((r) => r.data);
export const decideArtifact = (id, body) =>
  api.post(`/artifacts/${id}/decision`, body).then((r) => r.data);
export const aiDraft = (id, instruction) =>
  api
    .post(`/artifacts/${id}/ai-draft`, { artifact_id: id, instruction })
    .then((r) => r.data);
export const bulkAssign = (artifact_ids, reviewer) =>
  api.post("/artifacts/bulk-assign", { artifact_ids, reviewer }).then((r) => r.data);
export const bulkApprove = (artifact_ids, comment) =>
  api.post("/artifacts/bulk-approve", { artifact_ids, comment }).then((r) => r.data);
export const exportArtifacts = (params = {}) =>
  api
    .get("/artifacts/export", { params, responseType: "blob" })
    .then((r) => r.data);

export const listEvidence = (q, artifact) =>
  api.get("/evidence", { params: { ...(q ? { q } : {}), ...(artifact ? { artifact } : {}) } }).then((r) => r.data);
export const getEvidence = (id) => api.get(`/evidence/${id}`).then((r) => r.data);
export const exportEvidence = (id, format) =>
  api
    .get(`/evidence/${id}/export`, { params: { format }, responseType: "blob" })
    .then((r) => r.data);
export const exportEvidenceBulk = (format) =>
  api
    .get("/evidence/export/bulk", { params: { format }, responseType: "blob" })
    .then((r) => r.data);

export const listRevocations = () => api.get("/revocations").then((r) => r.data);
export const applyRevocationFreeze = (id) =>
  api.post(`/revocations/${id}/freeze`).then((r) => r.data);
export const rebuildRevocationDownstream = (id) =>
  api.post(`/revocations/${id}/rebuild`).then((r) => r.data);

export const listPolicies = () => api.get("/policies").then((r) => r.data);
export const getPolicy = (id) => api.get(`/policies/${id}`).then((r) => r.data);
export const createPolicy = (body) => api.post("/policies", body).then((r) => r.data);
export const updatePolicy = (id, body) => api.put(`/policies/${id}`, body).then((r) => r.data);
export const listPublished = () => api.get("/published").then((r) => r.data);

export const listIntegrations = () => api.get("/integrations").then((r) => r.data);
export const connectIntegration = (id, body = {}) =>
  api.post(`/integrations/${id}/connect`, body).then((r) => r.data);
export const resyncIntegration = (id) =>
  api.post(`/integrations/${id}/resync`).then((r) => r.data);
export const configureIntegration = (id, body) =>
  api.put(`/integrations/${id}/configure`, body).then((r) => r.data);

// Ghost blog subscribe (no auth required)
export const ghostSubscribe = (email) =>
  api.post("/ghost/subscribe", { email }).then((r) => r.data);

// Public tool endpoints (no auth required by backend)
export const classifyContributor = (description, source_type, domain) =>
  api
    .post("/tools/classify-contributor", { description, source_type, domain })
    .then((r) => r.data);
export const checkPolicy = (text, instruction) =>
  api.post("/tools/policy-check", { text, instruction }).then((r) => r.data);
